/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import ajax from 'helpers/ajax'
import RequestAction from 'store/RequestAction'
import {consoleLog} from 'utils/utility'
import {Reservation} from 'types/entities/Reservation'
import {ReservationNote} from 'types/entities/ReservationNote'
import {ReservationPlan} from 'types/entities/ReservationPlan'
import {ThunkAction} from 'model/store/thunk'
import {
	clearReservations,
	clearTableState,
	deleteReservation,
	deleteReservationNote,
	pushReservationNote,
	resetDefaultReservationsTable,
	setExtraInvoiceNoteToReservation,
	setExtraOfferNoteToReservation,
	setPackageCartToReservation,
	setReservation,
	setReservationPlans,
	setReservations,
	setReservationsTableUiState,
	setTableState,
} from 'store/reservations/ReservationsReducer'
import {ReservationsTableRequest} from 'types/requests/ReservationsTableRequest'
import {MassActionRequest, MassActionResponse} from 'types/requests/MassActionRequest'

export class ReservationsAction {
    static setReservations = setReservations
    static setTableState = setTableState
    static setReservationsTableUiState = setReservationsTableUiState
    static resetDefaultReservationsTable = resetDefaultReservationsTable
    static setReservation = setReservation
    static deleteReservation = deleteReservation
    static clearReservations = clearReservations
    static clearTableState = clearTableState
    static pushReservationNote = pushReservationNote
    static deleteReservationNote = deleteReservationNote
    static setPackageCartToReservation = setPackageCartToReservation
    static setReservationPlans = setReservationPlans
    static setExtraInvoiceNoteToReservation = setExtraInvoiceNoteToReservation
    static setExtraOfferNoteToReservation = setExtraOfferNoteToReservation

    static fetchExportReservationsXlsx = (params: {
        page?: number
        per_page?: number
        search?: ReservationsTableRequest['search']
        sorting?: { id: string; desc: boolean }[]
        sort_by?: string | null
        sort?: 'asc' | 'desc' | null
        hidden_columns?: string[]
        seller_id?: number
        filter?: ReservationsTableRequest['filter']
        view?: string | 'all'
        export?: 'xlsx' | 'csv' | 'pdf'
        [key: string]: unknown
    }): ThunkAction<Promise<string>> => {
        return async (_dispatch, getState): Promise<string> => {
            const url = '/admin/export/reservations'
            const hasClientSorting = params.sorting && params.sorting.length > 0
            const effectiveSorting = hasClientSorting
                ? params.sorting
                : [{ id: 'created_at', desc: true }]

            const response: { url: string } = await RequestAction.makeAuthenticatedRequest(
                getState,
                url,
                {
                    ...params,
                    page: params.page ?? 1,
                    per_page: params.per_page ?? 50,
                    view: params.view && params.view !== 'all' ? params.view : undefined,
                    search: params.search,
                    sorting: effectiveSorting,
                    export: params.export ?? 'xlsx',
                },
                'post',
                'json'
            )

            return response.url
        }
    }

    static fetchReservationsListForSeller = (seller_id: number) => {
        return async (dispatch) => {
            try {
                const response: {
                    reservations: Reservation[]
                } = await ajax.post(
                    '/admin/reservation/seller/list',
                    {
                        seller_id: seller_id,
                    },
                    {}
                )
                if (response?.reservations) {
                    dispatch(
                        ReservationsAction.setReservations(
                            response.reservations
                        )
                    )
                    return Promise.resolve(response.reservations)
                } else {
                    return Promise.reject(response)
                }
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static fetchMassAction = (
        payload: MassActionRequest
    ): ThunkAction<Promise<MassActionResponse>> => {
        return async (_dispatch, getState): Promise<MassActionResponse> => {
            return await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/reservations/mass-actions',
                payload,
                'post'
            )
        }
    }

    static fetchSetReservationPlan = (
        reservation_id: number,
        plan: ReservationPlan[]
    ): ThunkAction<Promise<ReservationPlan[]>> => {
        return async (dispatch): Promise<ReservationPlan[]> => {
            try {
                const response: {
                    reservation_plan: ReservationPlan[]
                } = await ajax.post(
                    '/admin/reservations/set-reservation-plan/' + reservation_id,
                    {
                        plan,
                    },
                    {}
                )
                if (response?.reservation_plan) {
                    dispatch(
                        ReservationsAction.setReservationPlans({
                            reservation_id,
                            plans: response.reservation_plan,
                        })
                    )
                    return Promise.resolve(response.reservation_plan)
                } else {
                    return Promise.reject(response)
                }
            } catch (err) {
                consoleLog('fetchSetReservationPlan - reject')
                consoleLog(err)
                return Promise.reject(err)
            }
        }
    }

    static fetchGetReservationPlan = (reservation_id: number): ThunkAction<Promise<string>> => {
        return async (): Promise<string> => {
            try {
                const response: {
                    plan: string
                } = await ajax.post('/admin/reservation/seller/get_reservation_plan/' + reservation_id, {}, {})
                if (response?.plan) {
                    return Promise.resolve(response.plan)
                } else {
                    return Promise.reject(response)
                }
            } catch (err) {
                consoleLog('fetchGetReservationPlan - reject')
                consoleLog(err)
                return Promise.reject(err)
            }
        }
    }

    static fetchSetInvoiceNoteToReservation = (reservation_id: number, invoice_note: string | null) => {
        return async (dispatch) => {
            try {
                const response: {
                    reservation: Reservation
                } = await ajax.post(
                    '/admin/reservations/set-invoice-note/' + reservation_id,
                    {
                        invoice_note: invoice_note,
                    },
                    {}
                )

                if (response?.reservation) {
                    dispatch(
                        ReservationsAction.setExtraInvoiceNoteToReservation({
                            reservation_id: reservation_id,
                            invoice_note: response.reservation.extra?.invoice_note || null,
                        })
                    )
                    return Promise.resolve(response)
                } else {
                    consoleLog('fetchSetInvoiceNoteToReservation - reject', response)
                    return Promise.reject(response)
                }
            } catch (err) {
                consoleLog('fetchSetInvoiceNoteToReservation - error', err)
                return Promise.reject(err)
            }
        }
    }

    /**
     * @deprecated
     */
    static fetchSetOfferNoteToReservation = (reservation_id: number, offer_note: string | null) => {
        return async (dispatch) => {
            try {
                const response: {
                    reservation: Reservation
                } = await ajax.post(
                    '/admin/reservations/set-offer-note/' + reservation_id,
                    {
                        offer_note: offer_note,
                    },
                    {}
                )

                if (response?.reservation) {
                    dispatch(
                        ReservationsAction.setExtraOfferNoteToReservation({
                            reservation_id: reservation_id,
                            offer_note: response.reservation.extra?.offer_note || null,
                        })
                    )
                    return Promise.resolve(response)
                } else {
                    consoleLog('fetchSetOfferNoteToReservation - reject', response)
                    return Promise.reject(response)
                }
            } catch (err) {
                consoleLog('fetchSetOfferNoteToReservation - error', err)
                return Promise.reject(err)
            }
        }
    }

    /**
     * @deprecated
     */
    static fetchAddReservationNote = (reservation_id: number, note: string, replied_note_id?: number | null) => {
        return async (dispatch) => {
            try {
                const response: {
                    success: boolean
                    note: ReservationNote
                } = await ajax.post('/admin/reservation/seller/add_note/' + reservation_id, {
                    note: note,
                    replied_note_id: replied_note_id,
                })

                if (response?.success) {
                    dispatch(ReservationsAction.pushReservationNote(response.note))
                    return Promise.resolve(response.note)
                } else {
                    return Promise.reject('add_note_failed')
                }
            } catch (err) {
                consoleLog('fetchAddReservationNote - error', err)
                return Promise.reject(err)
            }
        }
    }

    /**
     * @deprecated
     */
    static fetchEditReservationNote = (note_id: number, note: string) => {
        return async (dispatch) => {
            try {
                const response: {
                    success: boolean
                    note: ReservationNote
                } = await ajax.post('/admin/reservation/seller/edit_note/' + note_id, {
                    note: note,
                })

                if (response?.success) {
                    dispatch(ReservationsAction.pushReservationNote(response.note))
                    return Promise.resolve(response.note)
                } else {
                    return Promise.reject('edit_note_failed')
                }
            } catch (err) {
                consoleLog('fetchEditReservationNote - error', err)
                return Promise.reject(err)
            }
        }
    }

    /**
     * @deprecated
     */
    static fetchDeleteReservationNote = (reservation_id: number, note_id: number) => {
        return async (dispatch) => {
            try {
                const response: {
                    success: boolean
                } = await ajax.delete('/admin/reservation/seller/delete_note/' + note_id, {})

				console.log(response)
                if (response?.success) {
                    dispatch(
                        ReservationsAction.deleteReservationNote({
                            reservation_id,
                            note_id,
                        })
                    )
                    return Promise.resolve(true)
                } else {
                    return Promise.reject('delete_note_failed')
                }
            } catch (err) {
                consoleLog('fetchDeleteReservationNote - error', err)
                return Promise.reject(err)
            }
        }
    }

    static fetchReservationsListForSellerWithPagination = (
        seller_id: number,
        data: ReservationsTableRequest
    ) => {
        return () => {
            return ajax
                .post(
                    '/admin/reservation/seller/pagination',
                    {
                        per_page: data?.per_page,
                        page: data?.page,
                        search: data?.search && data.search,
                        sorting: data?.sorting,
                        seller_id: seller_id,
                    },
                    {}
                )
                .then(
                    (onResolve) => {
                        return Promise.resolve(onResolve.reservations)
                    },
                    (onReject) => {
                        console.log('fetch data error')
                        console.log(onReject)
                        return Promise.reject(onReject)
                    }
                )
                .catch((error) => {
                    if (error.name === 'CanceledError') {
                        console.log('Request aborted:', error.message)
                    } else {
                        console.error(error)
                    }
                    return Promise.reject(error)
                })
        }
    }
}

export default ReservationsAction
